import { ButtonPrimary } from '@/components/button'
import { Close } from '@/components/icons'
import { PhoneInput, TextInput } from '@/components/input'
import { duprCountries } from '@/constants/Countries'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { useEditClubStaff } from '@/services'
import { ClubStaff, Country } from '@/types'
import EditDirectorValidationSchema from '@/utils/schema/EditDirectorValidationSchema'
import { Dialog, Transition } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Fragment, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

type Props = {
  organizer: ClubStaff | null
  onClose: () => void
  title: string
  onRefresh: () => void
}

type DirectorEditForm = {
  name: string
  email: string
  phone: string
}

export const EditOrganizer = ({ organizer, onClose, title, onRefresh }: Props) => {
  const { t } = useTranslation()

  const { mutate: editClubStaff, isLoading } = useEditClubStaff()

  const [selectedCountry, setSelectedCountry] = useState<Country>(
    duprCountries.find((item) => Number(item.isoCode) === organizer?.isoAlpha2Code) || duprCountries[0]
  )

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<DirectorEditForm>({
    resolver: yupResolver(EditDirectorValidationSchema) as any,
    mode: 'onTouched',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
    },
  })

  const watchPhone = watch('phone')

  const onSubmit = handleSubmit(async (data) => {
    editClubStaff(
      {
        ...organizer,
        email: data.email,
        name: data.name,
        isoAlpha2Code: selectedCountry.isoCode,
        phone: `${selectedCountry.dialCode}${data.phone}`,
      },
      {
        onSuccess: () => {
          onRefresh()
          onClose()
          toast.success('Success')
        },
        onError: () => {
          toast.success(APP_CUSTOM_ERROR)
        },
      }
    )
  })

  useEffect(() => {
    if (organizer) {
      setValue('name', organizer?.name)
      setValue('email', organizer?.email)
      setValue('phone', organizer?.phone?.replace(selectedCountry.dialCode, ''))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizer])

  return (
    <Transition appear show={!!organizer} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col gap-y-4 max-w-lg transform overflow-hidden rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <button
                  className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-2 top-2"
                  onClick={onClose}
                >
                  <Close className="text-navy-900" />
                </button>

                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                  {title}
                </Dialog.Title>
                <div className="flex flex-col gap-y-4">
                  <div className="flex flex-col gap-y-2">
                    <TextInput
                      placeholder={t('common.name')}
                      labelClassName="!text-navy-900"
                      label={t('common.name')}
                      {...register('name')}
                      error={errors.name?.message}
                    />
                  </div>

                  <div className="flex flex-col gap-y-2">
                    <TextInput
                      placeholder={t('common.email')}
                      labelClassName="!text-navy-900"
                      label={t('common.email')}
                      {...register('email')}
                      error={errors.email?.message}
                    />
                  </div>

                  <div className="flex flex-col gap-y-2">
                    <PhoneInput
                      inputClassName="border border-navy-900 border-opacity-20"
                      label={t('common.phone')}
                      labelClassName="!text-navy-900"
                      placeholder={t('common.phone')}
                      maxLength={15}
                      selectedCountry={selectedCountry}
                      setSelectedCountry={setSelectedCountry}
                      phoneNumber={watchPhone}
                      setPhoneNumber={(phone: string) =>
                        setValue('phone', phone, { shouldDirty: true, shouldValidate: true })
                      }
                      error={errors.phone?.message}
                    />
                  </div>
                </div>
                <div className="flex justify-center gap-x-4 mt-4">
                  <ButtonPrimary
                    title={t('common.saveChanges')}
                    className={`!py-[6px] !rounded`}
                    onClick={onSubmit}
                    disabled={isLoading}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
